import { NavLink } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useEffect } from "react";
import {
  LayoutDashboard, Calendar, HandCoins, Send, BarChart3, FolderOpen, Users, Settings as Cog,
  LogOut, Church, UserSearch, X, Receipt, ScrollText,
} from "lucide-react";
import SidebarSystemStatus from "@/components/SidebarSystemStatus";

// Grouped nav. Section headers are visual-only; user can still scan the
// full list at a glance. Order reflects daily ministry workflow priority:
//   Today → Pulse (numbers) → Connect (people/comms) → Resources → Admin.
const NAV_GROUPS = [
  {
    label: "Today",
    items: [
      { to: "/", label: "Home", icon: LayoutDashboard, testid: "sidebar-nav-home", end: true },
      { to: "/calendar", label: "Calendar", icon: Calendar, testid: "sidebar-nav-calendar" },
    ],
  },
  {
    label: "Pulse",
    items: [
      { to: "/finance", label: "Finance", icon: HandCoins, testid: "sidebar-nav-finance" },
      { to: "/kpi", label: "KPI Dashboard", icon: BarChart3, testid: "sidebar-nav-kpi" },
    ],
  },
  {
    label: "Connect",
    items: [
      { to: "/mailchimp", label: "Mailchimp", icon: Send, testid: "sidebar-nav-mailchimp" },
      { to: "/people", label: "People", icon: UserSearch, testid: "sidebar-nav-people" },
      { to: "/staff", label: "Staff Directory", icon: Users, testid: "sidebar-nav-staff" },
    ],
  },
  {
    label: "Resources",
    items: [
      { to: "/documents", label: "Documents", icon: FolderOpen, testid: "sidebar-nav-documents" },
      { to: "/receipts", label: "Receipts", icon: Receipt, testid: "sidebar-nav-receipts" },
    ],
  },
];

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  // Build the live list. Admin gets an extra "Admin" group at the bottom.
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: [...g.items] }));
  if (user?.role === "admin") {
    groups.push({
      label: "Admin",
      items: [
        { to: "/audit-log", label: "Audit Log", icon: ScrollText, testid: "sidebar-nav-audit-log" },
        { to: "/settings", label: "Settings", icon: Cog, testid: "sidebar-nav-settings" },
      ],
    });
  }

  // Lock body scroll when the mobile drawer is open so background doesn't shift
  // under a thumb while the user is navigating.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <>
      {/* Mobile backdrop. Click-through dismiss. */}
      {mobileOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onMobileClose}
          className="fixed inset-0 z-30 bg-brand-primary/60 backdrop-blur-sm md:hidden animate-fade-in"
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-[#425252] text-white flex flex-col
          transition-transform duration-300 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0`}
      >
        <div className="px-6 py-7 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-terracotta grid place-items-center shadow-warm shrink-0">
              <Church className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <p className="font-heading text-lg font-semibold tracking-tight truncate">Family Chapel</p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/55">Staff Portal</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onMobileClose}
            className="md:hidden p-2 -mr-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10"
            data-testid="sidebar-close-button"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-5">
          {groups.map((group) => (
            <div key={group.label} data-testid={`sidebar-group-${group.label.toLowerCase()}`}>
              <p className="px-4 mb-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-white/35">
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all duration-300 ${
                        isActive
                          ? "bg-white/10 text-white"
                          : "text-white/65 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <it.icon
                          className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-peach" : "text-white/55 group-hover:text-white"}`}
                          strokeWidth={1.6}
                        />
                        <span className="font-medium">{it.label}</span>
                        {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-peach" />}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <SidebarSystemStatus />
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center overflow-hidden shrink-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                <span className="font-heading text-sm">{user?.name?.[0]?.toUpperCase() ?? "U"}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-peach/90">
                {ROLE_LABELS[user?.role] ?? "Staff"}
              </p>
            </div>
          </div>
          <button
            data-testid="sidebar-logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.6} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
